import { useRef, useState, type DragEvent } from 'react'
import {
  Upload,
  Download,
  FileSpreadsheet,
  CheckCircle2,
  AlertTriangle,
  Boxes,
  Car,
  X,
  Loader2,
} from 'lucide-react'

const API = '' // ruta relativa — el proxy de Vite reenvía a Next

type TabKey = 'stock' | 'compat'

interface StockResult {
  ok: true
  updated: number
  notFound: string[]
  errors: { row: number; sku: string; message: string }[]
}

interface CompatResult {
  ok: true
  created: number
  productNotFound: string[]
  errors: { row: number; sku: string; message: string }[]
}

const STOCK_TEMPLATE = `sku,stock
LH3K-001,15
LH3K-002,0
LH3K-003,42
`

const COMPAT_TEMPLATE = `product_sku,brand_name,model_name,year_from,year_to
LH3K-001,Toyota,Corolla,2014,2019
LH3K-001,Toyota,Yaris,2017,2022
LH3K-002,Nissan,Sentra,2013,2019
`

function downloadTemplate(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function ImportarDatos() {
  const [tab, setTab] = useState<TabKey>('stock')

  return (
    <div className="space-y-6 p-6">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-extrabold text-ink-900">
          <Upload className="size-6 text-brand-600" />
          Importar datos
        </h1>
        <p className="text-sm text-ink-500">
          Carga masiva por CSV de stock y compatibilidades.
        </p>
      </header>

      <div className="flex gap-2 border-b border-ink-200">
        <TabButton
          active={tab === 'stock'}
          onClick={() => setTab('stock')}
          icon={<Boxes className="size-4" />}
          label="Stock"
        />
        <TabButton
          active={tab === 'compat'}
          onClick={() => setTab('compat')}
          icon={<Car className="size-4" />}
          label="Compatibilidades"
        />
      </div>

      {tab === 'stock' ? <StockTab /> : <CompatTab />}
    </div>
  )
}

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px inline-flex items-center gap-2 border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${
        active
          ? 'border-brand-600 text-brand-700'
          : 'border-transparent text-ink-500 hover:text-ink-700'
      }`}
    >
      {icon}
      {label}
    </button>
  )
}

/* ------------------------------------------------------------------ */
/* Tab: Stock                                                          */
/* ------------------------------------------------------------------ */

function StockTab() {
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState<StockResult | null>(null)
  const [error, setError] = useState('')

  async function upload() {
    if (!file) return
    setUploading(true)
    setError('')
    setResult(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await fetch(`${API}/api/admin/inventario/import`, {
        method: 'POST',
        credentials: 'same-origin',
        body: fd,
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) {
        setError(data.error || `HTTP ${res.status}`)
        return
      }
      setResult(data as StockResult)
    } catch (e: any) {
      setError(e?.message || 'Error de red')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-5">
      <TemplateCard
        title="1. Descarga la plantilla"
        description='Columnas: "sku" y "stock" (también acepta "quantity"). Se actualiza por SKU exacto.'
        filename="plantilla-stock.csv"
        onDownload={() => downloadTemplate('plantilla-stock.csv', STOCK_TEMPLATE)}
      />

      <DropZone
        file={file}
        onFile={setFile}
        onUpload={upload}
        uploading={uploading}
        error={error}
        hint="CSV con columnas sku,stock (o sku,quantity)"
      />

      {result && (
        <section className="rounded-2xl border border-ink-200 bg-white p-5">
          <div className="mb-4 flex items-center gap-2 border-b border-ink-100 pb-3">
            <CheckCircle2 className="size-5 text-emerald-600" />
            <h2 className="font-semibold text-ink-900">Resultado</h2>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Actualizados" value={result.updated} color="emerald" />
            <Stat
              label="SKUs no encontrados"
              value={result.notFound.length}
              color={result.notFound.length ? 'amber' : 'ink'}
            />
            <Stat
              label="Errores"
              value={result.errors.length}
              color={result.errors.length ? 'red' : 'ink'}
            />
          </div>

          {result.notFound.length > 0 && (
            <ListSection
              title="SKUs no encontrados"
              variant="amber"
              items={result.notFound.map((sku) => sku)}
            />
          )}

          {result.errors.length > 0 && (
            <ListSection
              title="Errores"
              variant="red"
              items={result.errors.map(
                (e) => `Fila ${e.row} · ${e.sku || '(sin sku)'}: ${e.message}`,
              )}
            />
          )}
        </section>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Tab: Compatibilidades                                               */
/* ------------------------------------------------------------------ */

function CompatTab() {
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState<CompatResult | null>(null)
  const [error, setError] = useState('')

  async function upload() {
    if (!file) return
    setUploading(true)
    setError('')
    setResult(null)
    try {
      const fd = new FormData()
      fd.append('file', file)
      const res = await fetch(`${API}/api/admin/compatibilidades/import`, {
        method: 'POST',
        credentials: 'same-origin',
        body: fd,
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) {
        setError(data.error || `HTTP ${res.status}`)
        return
      }
      setResult(data as CompatResult)
    } catch (e: any) {
      setError(e?.message || 'Error de red')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-5">
      <TemplateCard
        title="1. Descarga la plantilla"
        description='Columnas: "product_sku", "brand_name", "model_name", "year_from", "year_to". Marcas y modelos se crean si no existen.'
        filename="plantilla-compatibilidades.csv"
        onDownload={() =>
          downloadTemplate('plantilla-compatibilidades.csv', COMPAT_TEMPLATE)
        }
      />

      <DropZone
        file={file}
        onFile={setFile}
        onUpload={upload}
        uploading={uploading}
        error={error}
        hint="CSV con product_sku, brand_name, model_name, year_from, year_to"
      />

      {result && (
        <section className="rounded-2xl border border-ink-200 bg-white p-5">
          <div className="mb-4 flex items-center gap-2 border-b border-ink-100 pb-3">
            <CheckCircle2 className="size-5 text-emerald-600" />
            <h2 className="font-semibold text-ink-900">Resultado</h2>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Compatibilidades creadas" value={result.created} color="emerald" />
            <Stat
              label="SKUs no encontrados"
              value={result.productNotFound.length}
              color={result.productNotFound.length ? 'amber' : 'ink'}
            />
            <Stat
              label="Errores"
              value={result.errors.length}
              color={result.errors.length ? 'red' : 'ink'}
            />
          </div>

          {result.productNotFound.length > 0 && (
            <ListSection
              title="SKUs no encontrados"
              variant="amber"
              items={result.productNotFound}
            />
          )}

          {result.errors.length > 0 && (
            <ListSection
              title="Errores"
              variant="red"
              items={result.errors.map(
                (e) => `Fila ${e.row} · ${e.sku || '(sin sku)'}: ${e.message}`,
              )}
            />
          )}
        </section>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Sub-componentes compartidos                                          */
/* ------------------------------------------------------------------ */

function TemplateCard({
  title,
  description,
  filename,
  onDownload,
}: {
  title: string
  description: string
  filename: string
  onDownload: () => void
}) {
  return (
    <section className="rounded-2xl border border-ink-200 bg-white p-5">
      <div className="flex items-start gap-3">
        <FileSpreadsheet className="mt-0.5 size-5 text-brand-600" />
        <div className="flex-1">
          <h2 className="font-semibold text-ink-900">{title}</h2>
          <p className="text-sm text-ink-500">{description}</p>
          <button
            type="button"
            onClick={onDownload}
            className="mt-2 inline-flex items-center gap-2 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-semibold text-brand-700 hover:border-brand-300"
          >
            <Download className="size-4" /> {filename}
          </button>
        </div>
      </div>
    </section>
  )
}

function DropZone({
  file,
  onFile,
  onUpload,
  uploading,
  error,
  hint,
}: {
  file: File | null
  onFile: (f: File | null) => void
  onUpload: () => void
  uploading: boolean
  error: string
  hint: string
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  function onDrop(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files?.[0]
    if (f) onFile(f)
  }

  return (
    <section className="rounded-2xl border border-ink-200 bg-white p-5">
      <h2 className="mb-3 font-semibold text-ink-900">2. Sube tu CSV</h2>

      <label
        htmlFor="csv-import-file"
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-12 transition-colors ${
          dragOver
            ? 'border-brand-500 bg-brand-50'
            : 'border-ink-200 bg-ink-50 hover:border-brand-400 hover:bg-brand-50/50'
        }`}
      >
        <Upload className="size-10 text-ink-400" />
        <div className="text-center">
          <p className="font-semibold text-ink-700">
            {file ? file.name : 'Arrastra el CSV aquí o haz clic para seleccionar'}
          </p>
          <p className="mt-1 text-xs text-ink-500">{hint}</p>
        </div>
        <input
          ref={inputRef}
          id="csv-import-file"
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            onFile(e.target.files?.[0] ?? null)
            e.target.value = ''
          }}
        />
      </label>

      {file && (
        <div className="mt-4 flex items-center justify-between rounded-lg border border-ink-200 bg-ink-50 p-3 text-sm">
          <div className="min-w-0 flex-1">
            <div className="truncate font-medium text-ink-900">{file.name}</div>
            <div className="text-xs text-ink-500">
              {(file.size / 1024).toFixed(1)} KB
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onFile(null)}
              disabled={uploading}
              className="grid size-8 place-items-center rounded-md border border-ink-200 bg-white text-ink-600 hover:bg-ink-100 disabled:opacity-50"
              title="Quitar archivo"
            >
              <X className="size-4" />
            </button>
            <button
              type="button"
              onClick={onUpload}
              disabled={uploading}
              className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {uploading ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Upload className="size-4" />
              )}
              {uploading ? 'Procesando…' : 'Subir e importar'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 size-4" />
          {error}
        </div>
      )}
    </section>
  )
}

function Stat({
  label,
  value,
  color = 'ink',
}: {
  label: string
  value: number
  color?: 'ink' | 'emerald' | 'brand' | 'red' | 'amber'
}) {
  const colors: Record<string, string> = {
    ink: 'text-ink-900 bg-ink-50',
    emerald: 'text-emerald-700 bg-emerald-50',
    brand: 'text-brand-700 bg-brand-50',
    red: 'text-red-700 bg-red-50',
    amber: 'text-amber-700 bg-amber-50',
  }
  return (
    <div className={`rounded-xl p-3 text-center ${colors[color]}`}>
      <div className="text-2xl font-extrabold">
        {value.toLocaleString('es-MX')}
      </div>
      <div className="text-xs uppercase tracking-wider opacity-70">{label}</div>
    </div>
  )
}

function ListSection({
  title,
  items,
  variant,
}: {
  title: string
  items: string[]
  variant: 'red' | 'amber'
}) {
  const styles =
    variant === 'red'
      ? {
          header: 'text-red-700',
          item: 'border-red-100 bg-red-50 text-red-800',
        }
      : {
          header: 'text-amber-700',
          item: 'border-amber-100 bg-amber-50 text-amber-800',
        }
  return (
    <div className="mt-4">
      <h3 className={`mb-2 text-sm font-semibold ${styles.header}`}>
        {title} ({items.length})
      </h3>
      <ul className="max-h-64 space-y-1 overflow-y-auto text-xs">
        {items.map((it, i) => (
          <li
            key={i}
            className={`break-words rounded border p-2 font-mono ${styles.item}`}
          >
            {it}
          </li>
        ))}
      </ul>
    </div>
  )
}

export default ImportarDatos
